import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, Home, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getSignedUrl,
  useFinalizeProtocol,
  useProtocol,
  useProtocolBooking,
  useProtocolDefects,
  useProtocolInventory,
  useProtocolPhotos,
} from "@/hooks/useProtocol";
import { isAdminRecorded } from "@/lib/protocol";
import { formatMsg, type ProtocolCopy } from "@/lib/protocol-copy";
import { downloadProtocolPdf, generateProtocolPdf } from "@/lib/protocol-pdf";

export function DoneStep({
  protocolId,
  t,
  onClose,
}: {
  protocolId: string;
  t: ProtocolCopy;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { data: protocol } = useProtocol(protocolId);
  const { data: booking } = useProtocolBooking(protocol?.booking_id ?? null);
  const { data: photos = [] } = useProtocolPhotos(protocolId);
  const { data: inventory = [] } = useProtocolInventory(protocolId);
  const { data: defects = [] } = useProtocolDefects(protocolId);
  const [generating, setGenerating] = useState(false);
  const finalize = useFinalizeProtocol(protocolId);
  const finalizedOnceRef = useRef(false);

  // Render + upload the PDF, then trigger the confirmation email — only
  // the first time DoneStep mounts for a freshly-submitted protocol.
  // Idempotent on the server side too: `pdf_storage_path` and
  // `email_sent_at` short-circuit duplicate work. (COL-211 / COL-214)
  useEffect(() => {
    if (finalizedOnceRef.current) return;
    if (!protocol || !booking) return;
    if (protocol.status !== "submitted") return;
    if (protocol.pdf_storage_path && protocol.email_sent_at) return;
    // An operator recording already has its PDF + attribution written at
    // record time (useRecordProtocolOnBehalf) — it has no photos/inventory
    // for this tenant-walkthrough finalizer to render, and its email (if
    // any) is sent from that same mutation, not from here.
    if (isAdminRecorded(protocol)) return;
    finalizedOnceRef.current = true;

    (async () => {
      try {
        const blob = await generateProtocolPdf({
          protocol,
          photos,
          inventory,
          defects,
          booking: booking
            ? {
                property_name: booking.property_name,
                room_number: booking.room_number,
                address: booking.address,
                move_in_date: booking.move_in_date ?? null,
                move_out_date: booking.move_out_date ?? null,
                deposit_amount: booking.deposit_amount ?? null,
              }
            : null,
          tenant: {
            name: (user?.user_metadata?.name as string | undefined) ?? null,
            email: user?.email ?? null,
          },
          language: language === "de" ? "de" : "en",
        });
        finalize.mutate({
          pdfBlob: blob,
          pdfFilename: `${protocol.ref}.pdf`,
          language: language === "de" ? "de" : "en",
          existingPdfPath: protocol.pdf_storage_path,
          alreadyEmailed: !!protocol.email_sent_at,
        });
      } catch (err) {
        console.warn("PDF finalize failed (non-blocking)", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    protocol?.id,
    protocol?.status,
    protocol?.pdf_storage_path,
    protocol?.email_sent_at,
    booking?.id,
    photos.length,
    inventory.length,
    defects.length,
  ]);

  if (!protocol) return null;

  const email = user?.email ?? "";

  const handleDownload = async () => {
    setGenerating(true);
    try {
      // An operator recording's evidence is the PDF filed at record time
      // (attachments merged with an attestation page) — it has no photos/
      // inventory/signature rows for the tenant-walkthrough generator below
      // to render, so fetch the stored file instead of regenerating one.
      if (isAdminRecorded(protocol) && protocol.pdf_storage_path) {
        const url = await getSignedUrl(protocol.pdf_storage_path);
        if (!url) throw new Error("Could not produce a download link.");
        const res = await fetch(url);
        if (!res.ok) throw new Error("Could not fetch the filed document.");
        downloadProtocolPdf(await res.blob(), protocol.ref);
        return;
      }
      const blob = await generateProtocolPdf({
        protocol,
        photos,
        inventory,
        defects,
        booking: booking
          ? {
              property_name: booking.property_name,
              room_number: booking.room_number,
              address: booking.address,
            }
          : null,
        tenant: {
          name: (user?.user_metadata?.name as string | undefined) ?? null,
          email: user?.email ?? null,
        },
        language: language === "de" ? "de" : "en",
      });
      downloadProtocolPdf(blob, protocol.ref);
    } catch {
      toast.error(t.viewPdfFailed);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex justify-center py-8 md:py-12">
      <div className="w-full max-w-xl rounded-3xl bg-white px-6 py-12 text-center md:border md:border-neutral-200 md:px-12 md:py-16 md:shadow-sm">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/15 ring-8 ring-emerald-500/5">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white">
            <CheckCircle2 className="h-8 w-8" />
          </span>
        </div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
          {protocol.kind === "move_in" ? t.moveIn : t.moveOut}
        </div>
        <h1 className="mt-3 text-[26px] font-bold tracking-tight text-neutral-900 md:text-3xl">
          {formatMsg(t.doneTitleKind, {
            kind: protocol.kind === "move_in" ? t.moveIn : t.moveOut,
          })}
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-snug text-neutral-500">
          {t.doneSubtitle}
        </p>
        {email ? (
          <div
            className="mx-auto mt-3 inline-block max-w-[calc(100%-1rem)] break-all rounded-full bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-700"
            title={email}
          >
            {email}
          </div>
        ) : null}

        <div className="mx-auto mt-6 inline-flex items-center rounded-xl border border-neutral-200 px-4 py-2 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            {t.refLabel}
          </span>
          <span className="ml-2 font-mono font-semibold text-neutral-900">
            {protocol.ref}
          </span>
        </div>

        <div className="mt-8 flex flex-col gap-3 md:flex-row md:justify-center">
          <button
            type="button"
            onClick={handleDownload}
            disabled={generating}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-neutral-900 px-6 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-neutral-400"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t.downloadPdf}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-neutral-200 bg-white px-6 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <Home className="h-4 w-4" />
            {t.toHome}
          </button>
        </div>
      </div>
    </div>
  );
}
